import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { authApi, ApiError, getUserProfile, setUserProfile } from '@/lib/api'
import HomeNav from '@/components/home-nav'
import HomeFooter from '@/components/home-footer'
import { Button } from '@/components/ui/button'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid or missing verification token.')
      return
    }

    authApi.verifyEmail(token)
      .then(() => {
        const profile = getUserProfile()
        if (profile) setUserProfile({ ...profile, is_email_verified: true })
        setStatus('success')
      })
      .catch((err) => {
        setStatus('error')
        setMessage(err instanceof ApiError ? err.message : 'Verification failed. The link may have expired.')
      })
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <HomeNav />
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full mx-auto" />
          {status === 'loading' && (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-secondary mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[#0D0D0D]">Verifying your email…</h1>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-10 w-10 text-[#1D591B] mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[#0D0D0D] mb-2">Email verified!</h1>
              <p className="text-sm text-[#0D0D0D]/60 mb-6">Your email has been successfully verified.</p>
              <Button asChild className="bg-[#1D591B] text-white hover:bg-[#1D591B]/85 rounded-none px-6 font-semibold text-sm tracking-wide h-10">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
              <h1 className="text-xl font-bold text-[#0D0D0D] mb-2">Verification failed</h1>
              <p className="text-sm text-[#0D0D0D]/60">{message}</p>
            </>
          )}
        </div>
      </main>
      <HomeFooter />
    </div>
  )
}
